























































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import { InputMassMessage, MassMessage } from "@scrile/api-provider/dist/api/MassMessageProvider";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BasePage from "@/components/BasePage";
import BaseSpinner from "@/components/BaseSpinner";
import GroupListSelection from "@/components/GroupListSelection";
import PageTitle from "@/components/PageTitle";
import MassMessageConfirm from "./components/MassMessageConfirm.vue";
import MassMessageEditView from "./components/MassMessageEditView.vue";

export default defineComponent({
  name: "MassMessageEdit",
  props: {
    loadingGroups: { type: Boolean, default: false },
    groupList: { type: Array as () => Group[], required: true },
    massMessage: { type: Object as () => MassMessage },
    defaultBack: { type: Object },
    selectedGroupIds: { type: Array as () => string[] },
  },
  components: {
    BaseSpinner,
    BaseIcon,
    BaseIconButton,
    BasePage,
    PageTitle,
    MassMessageConfirm,
    MassMessageEditView,
    GroupListSelection,
  },
  setup(props, { emit }) {
    const selectedIds = ref(props.massMessage ? props.massMessage.groupIds : props.selectedGroupIds ?? []);
    const excludedIds = ref(props.massMessage?.excludedGroupIds ?? []);
    const recipientCount = ref(0);
    const loadingCount = ref(false);

    const isGroupMode = ref(false);
    const isShowCancelConfirm = ref(false);
    const resetGroupsSelection = ref<() => void>();
    const loading = ref(false);
    const submitting = ref(false);

    const isEditMode = computed(() => !!props.massMessage?.id);
    const vOnTitle = computed(() => {
      if (!isGroupMode.value && !props.massMessage) {
        return undefined;
      }
      if (isGroupMode.value && selectedIds.value.length === 0 && !props.massMessage) {
        return undefined;
      }

      return {
        click: () => {
          if (!isGroupMode.value && props.massMessage) {
            emit("back");
          } else {
            isGroupMode.value = false;
            resetGroupsSelection.value && resetGroupsSelection.value();
          }
        },
      };
    });
    const title = computed(() => {
      if (isGroupMode.value) return $t("Select audience");
      if (isEditMode.value) return $t("Edit");
      return $t("Create a mass message");
    });
    const onSubmit = async (data: InputMassMessage) => {
      try {
        submitting.value = true;
        const updatedMassMessage = await providers.MassMessageProvider.update({ data });
        emit("save", updatedMassMessage);
      } finally {
        submitting.value = false;
      }
    };
    const onConfirm = async () => {
      if (!props.massMessage) return;
      try {
        loading.value = true;
        const result = await providers.MassMessageProvider.cancel({ id: props.massMessage.id });
        result && emit("delete", props.massMessage.id);
      } finally {
        isShowCancelConfirm.value = false;
        loading.value = false;
      }
    };
    const onContinue = async () => {
      isGroupMode.value = false;
    };

    const getRecipientCount = debounce(async () => {
      try {
        loadingCount.value = true;
        recipientCount.value = await providers.GroupsProvider.uniqueMemberCount({
          data: {
            groupIds: selectedIds.value,
            excludeGroupIds: excludedIds.value,
          },
        });
      } finally {
        loadingCount.value = false;
      }
    }, 500);

    const onSelectGroup = async ({ status, groupId }: { status: boolean; groupId: string }) => {
      if (status) {
        selectedIds.value.push(groupId);
        excludedIds.value = excludedIds.value.filter(i => i !== groupId);
      } else {
        selectedIds.value = selectedIds.value.filter(i => i !== groupId);
      }
      await getRecipientCount();
    };

    const onExcludeGroup = async ({ status, groupId }: { status: boolean; groupId: string }) => {
      if (status) {
        excludedIds.value.push(groupId);
        selectedIds.value = selectedIds.value.filter(i => i !== groupId);
      } else {
        excludedIds.value = excludedIds.value.filter(i => i !== groupId);
      }
      await getRecipientCount();
    };

    const getData = () => {
      if (props.massMessage || props.selectedGroupIds?.length) {
        getRecipientCount();
      }
    };

    getData();

    watch(
      recipientCount,
      () => {
        if (
          recipientCount.value === 0 &&
          !props.massMessage &&
          (!props.selectedGroupIds || props.selectedGroupIds.length === 0)
        ) {
          isGroupMode.value = true;
        }
      },
      { immediate: true }
    );

    return {
      onSelectGroup,
      onExcludeGroup,
      selectedIds,
      excludedIds,
      recipientCount,
      loadingCount,
      title,
      loading,
      submitting,
      vOnTitle,
      isEditMode,
      isGroupMode,
      isShowCancelConfirm,
      onSubmit,
      onConfirm,
      onContinue,
    };
  },
});
